import axios from 'axios';
import jwtDecode from 'jwt-decode';
// config
import { HOST_API } from '../config';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({
  baseURL: HOST_API,
});

// Endpoint that renews an impersonation session token (see backend
// ImpersonateController::refresh). Kept in sync with the route registered in
// Modules/Backoffice/Routes/api.php.
const IMPERSONATION_REFRESH_URL = '/app/backoffice/impersonate/refresh';

// True only while the current access token is an impersonation token. Used to
// scope the 401 auto-refresh below so NON-impersonation sessions keep the
// exact original behaviour (plain reject, no retry).
const isImpersonationSession = (): boolean => {
  const token = localStorage.getItem('accessToken');
  if (!token) return false;
  try {
    return jwtDecode<{ impersonating?: boolean }>(token).impersonating === true;
  } catch {
    return false;
  }
};

// Single-flight guard: concurrent 401s trigger only one refresh call; every
// waiting request reuses the same in-flight promise and its resulting token.
let refreshPromise: Promise<string> | null = null;

const refreshImpersonationToken = (): Promise<string> => {
  if (!refreshPromise) {
    refreshPromise = axiosInstance
      .post(IMPERSONATION_REFRESH_URL)
      .then((res) => {
        const newToken = res.data.access_token as string;
        localStorage.setItem('accessToken', newToken);
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${newToken}`;
        return newToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // Impersonation-only: on a 401, silently renew the impersonation session
    // token and retry the request once. Everything below is skipped for normal
    // sessions, so their behaviour is byte-for-byte unchanged.
    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._impersonationRetry &&
      !originalRequest.url?.includes(IMPERSONATION_REFRESH_URL) &&
      isImpersonationSession()
    ) {
      originalRequest._impersonationRetry = true;
      try {
        const newToken = await refreshImpersonationToken();
        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return await axiosInstance(originalRequest);
      } catch {
        // Refresh failed (past the absolute cap, user no longer active, token
        // blacklisted after leave, ...). Fall through to the original rejection
        // so the app can route the operator back to re-impersonate.
      }
    }

    return Promise.reject((error.response && error.response.data) || 'Something went wrong');
  }
);

export default axiosInstance;
